import { useState } from 'react';
import { Alert, Input, Label, Modal, TextField } from '@heroui/react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import { ColorSwatchPicker } from '@/components/ColorSwatchPicker';
import { api } from '@/lib/api';
import { humanizeApiError } from '@/lib/humanizeApiError';
import type { GuildFolder } from '@/types/api';
import { FOLDER_NAME_MAX } from './folderRules';

/**
 * Rename / recolour a server folder.
 *
 * Folders were created by the drag-onto-another gesture and there was no way to
 * name one afterwards — the API has taken `name` and `color` since day one, but
 * nothing in the UI ever sent them, so every folder in the rail was nameless
 * and every folder spine was the app accent.
 *
 * Two fields only. A folder is a shelf, not an entity with settings: it holds
 * a label and a colour, and «Расформировать» already lives in the rail's
 * context menu next to the entry that opens this dialog.
 */
export function FolderSettingsModal(props: {
  folder: GuildFolder;
  /** Server names inside the folder — shown as the placeholder, so the field
   *  hints what an unnamed folder is currently called in tooltips. */
  memberNames: string[];
  onSaved: (folder: GuildFolder) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(props.folder.name ?? '');
  const [color, setColor] = useState<string | null>(props.folder.color ?? null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmed = name.trim();
  const nextName = trimmed || null;
  const nextColor = color ?? null;
  const dirty = nextName !== (props.folder.name ?? null) || nextColor !== (props.folder.color ?? null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy || !dirty) return;
    setBusy(true);
    setError(null);
    try {
      // Both fields go every time: clearing the name back to empty has to reach
      // the server as null, which is exactly what it accepts to unset it.
      const folder = await api.updateGuildFolder(props.folder.id, {
        name: nextName,
        color: nextColor,
      });
      props.onSaved(folder);
      props.onClose();
    } catch (err) {
      const h = humanizeApiError(err, t('ui.topbar.folderSaveFailed'));
      setError(h.hint ? `${h.title}. ${h.hint}` : h.title);
      setBusy(false);
    }
  }

  return (
    <Modal.Backdrop
      isOpen
      onOpenChange={(open) => {
        if (!open) props.onClose();
      }}
    >
      <Modal.Container size="sm" className="app-panel-container">
        <Modal.Dialog className="app-panel-dialog app-folder-dialog">
          <Modal.Header className="app-panel-titlebar">
            <Modal.Heading>{t('ui.topbar.folderSettings')}</Modal.Heading>
            <Modal.CloseTrigger aria-label={t('common.close')} />
          </Modal.Header>
          <Modal.Body>
            <form onSubmit={submit} className="flex flex-col gap-4">
              <TextField fullWidth value={name} onChange={setName}>
                <Label>{t('ui.topbar.folderNameLabel')}</Label>
                <Input
                  autoFocus
                  variant="secondary"
                  // An unnamed folder is called by its contents everywhere else,
                  // so the placeholder shows exactly that instead of «Название».
                  placeholder={props.memberNames.join(', ')}
                  maxLength={FOLDER_NAME_MAX}
                />
              </TextField>

              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-muted">
                  {t('ui.topbar.folderColorLabel')}
                </span>
                <div className="flex items-center gap-2">
                  <ColorSwatchPicker
                    value={color ?? 'rgb(var(--bk-accent))'}
                    onChange={setColor}
                  />
                  {color && (
                    <Button variant="ghost" size="sm" onClick={() => setColor(null)}>
                      {t('ui.topbar.folderColorReset')}
                    </Button>
                  )}
                </div>
              </div>

              {error && (
                // role=alert so the failure is announced, not just drawn.
                <Alert status="danger" role="alert">
                  <Alert.Indicator />
                  <Alert.Content>
                    <Alert.Title>{error}</Alert.Title>
                  </Alert.Content>
                </Alert>
              )}

              <div className="flex items-center justify-end gap-2 pt-1">
                <Button variant="ghost" size="sm" onClick={props.onClose}>
                  {t('gmod.cancel')}
                </Button>
                <Button variant="primary" size="sm" type="submit" disabled={!dirty} loading={busy}>
                  {t('gmod.save')}
                </Button>
              </div>
            </form>
          </Modal.Body>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
}
